import {useState, useCallback, useMemo, useEffect, useRef} from 'react';
import API from '../../../../network/dashboardAPI';
import useAlert from '../../../../services/useAlert';
import {validateOrderItems, formatValidationErrors, getFieldError} from '../utils/validation';
import {getProductVatRate} from '../utils/vat';

/**
 * Map numeric item_status to string status
 * 0=New, 1=Picking, 2=Picked, 3=Reserved, 4=Sorted, 5=Cancelled, 6=Change Requested
 */
const mapItemStatusToString = (statusCode) => {
	const statusMap = {
		0: 'new',
		1: 'picking',
		2: 'picked',
		3: 'reserved',
		4: 'sorted',
		5: 'cancelled',
		6: 'change_requested',
	};
	return statusMap[statusCode] || 'new';
};

/**
 * Map string status to numeric item_status
 */
const mapStringStatusToItemStatus = (status) => {
	const statusMap = {
		'new': 0,
		'picking': 1,
		'picked': 2,
		'reserved': 3,
		'sorted': 4,
		'cancelled': 5,
		'change_requested': 6,
	};
	return statusMap[status] !== undefined ? statusMap[status] : 0;
};

/**
 * Custom hook for managing order items state and operations
 */
export const useOrderItems = (initialDetails, orderId, driverTip, onDriverTipChange, deliveryFee, onDeliveryFeeChange, refreshOrder, pendingAddress, onAddressSaved, selectedCouponId = null, onTotalsChange = null, onOrderSummeryChange = null) => {
	const alert = useAlert();

	// Store initial driver tip from initialDetails for comparison
	const [initialDriverTip, setInitialDriverTip] = useState(() => {
		return parseFloat(initialDetails?.order_payment_summery?.driver_tip || initialDetails?.driver_tip || 0);
	});

	// Store initial delivery fee from initialDetails for comparison
	const [initialDeliveryFee, setInitialDeliveryFee] = useState(() => {
		return parseFloat(initialDetails?.delivery_fee || 0);
	});

	// Store initial coupon ID for comparison
	const [initialCouponId, setInitialCouponId] = useState(() => {
		return initialDetails?.coupon_code_id || null;
	});

	// Helper function to map item details
	const mapItemDetails = (items) => items?.map((item, index) => ({
		...item,
		id: index,
		status: mapItemStatusToString(item.item_status),
		cancelReason: item.cancel_reason || '',
		comment: item.comment || '',
		originalPrice: item.original_price ?? null,
		replaced_from: item.replaced_from || null,
		replacement_items: item.replacement_items || [],
		// Ensure coupon_discount_amount is set (API may return as coupon_discount)
		coupon_discount_amount: item.coupon_discount_amount || item.coupon_discount || 0,
		// Track if discount was manually edited (defaults to false for items from server)
		is_manual_discount: item.is_manual_discount || false,
	})) || [];

	const [orderItems, setOrderItems] = useState(() => mapItemDetails(initialDetails?.order_item_details));

	// Track the "saved" baseline for comparison (updates after successful save)
	const [savedItemsBaseline, setSavedItemsBaseline] = useState(() =>
		JSON.stringify(mapItemDetails(initialDetails?.order_item_details))
	);
	const [hasChanges, setHasChanges] = useState(false);
	// Flag to track if hasChanges was forced (e.g., by clearing coupon discounts)
	const [forcedChanges, setForcedChanges] = useState(false);
	const [isSaving, setIsSaving] = useState(false);
	const [isLoading, setIsLoading] = useState(false);
	const [isRecalculating, setIsRecalculating] = useState(false);
	const [lastSaved, setLastSaved] = useState(initialDetails?.updatedAt || null);
	const [validationErrors, setValidationErrors] = useState({});

	// ---------------------------------------------------------------------------
	// Settle gate: props like driverTip, deliveryFee, selectedCouponId are owned by
	// the parent and arrive asynchronously after setDetails mounts this component.
	// Baselines (initialDriverTip, initialDeliveryFee, initialCouponId) are captured
	// from initialDetails on mount and reflect the correct server values.  The props
	// may still hold their default values (0 / null) at that point, producing a
	// spurious diff.  isSettled stays false — and dirty tracking is suppressed —
	// until every prop matches the value we expect from the server.
	// ---------------------------------------------------------------------------
	const expectedInitialRef = useRef({
		tip: parseFloat(initialDetails?.order_payment_summery?.driver_tip || initialDetails?.driver_tip || 0),
		fee: parseFloat(initialDetails?.delivery_fee || 0),
		coupon: initialDetails?.coupon_code_id || null,
	});

	const [isSettled, setIsSettled] = useState(() =>
		parseFloat(driverTip || 0) === expectedInitialRef.current.tip &&
		parseFloat(deliveryFee || 0) === expectedInitialRef.current.fee &&
		(selectedCouponId || null) === expectedInitialRef.current.coupon
	);

	// Ref to store recalculate handler for use in other handlers
	const recalculateRef = useRef(null);
	const hasRecalculatedRef = useRef(false);
	const pendingRecalcRef = useRef(false);

	// Fetch order details from API
	const fetchOrderDetails = useCallback(async () => {
		if (!orderId) return;

		setIsLoading(true);
		try {
			const response = await API.getOrderDetails(orderId);

			if (response && response.success && response.data) {
				const items = mapItemDetails(response.data.order_item_details);

				setOrderItems(items);
				setSavedItemsBaseline(JSON.stringify(items));
				setInitialCouponId(response.data.coupon_code_id || null); // Update initial coupon ID
				setForcedChanges(false); // Reset forced changes flag
				hasRecalculatedRef.current = false;
				setLastSaved(response.data.updatedAt || null);
				setHasChanges(false);
			} else {
				throw new Error(response?.message || response?.data?.message || 'Failed to fetch order details');
			}
		} catch (error) {
			alert.toast(`Failed to load order details: ${error.message || error}`, 'error');
		} finally {
			setIsLoading(false);
		}
	}, [orderId, alert]);

	// Wait for parent-owned props to reach their server values before enabling dirty tracking
	useEffect(() => {
		if (isSettled) return;
		if (
			parseFloat(driverTip || 0) === expectedInitialRef.current.tip &&
			parseFloat(deliveryFee || 0) === expectedInitialRef.current.fee &&
			(selectedCouponId || null) === expectedInitialRef.current.coupon
		) {
			setIsSettled(true);
		}
	}, [isSettled, driverTip, deliveryFee, selectedCouponId]);
	// Auto-recalculate coupon distribution after Replace / Add item
	useEffect(() => {
		if (pendingRecalcRef.current && !isRecalculating && recalculateRef.current) {
			pendingRecalcRef.current = false;
			recalculateRef.current();
		}
	}, [orderItems, isRecalculating]);

	// Track if there are unsaved changes (items OR driver tip OR delivery fee OR address OR coupon OR forced)
	useEffect(() => {
		// forcedChanges is always set explicitly by user action — honour it first
		if (forcedChanges) {
			setHasChanges(true);
			return;
		}

		// Props have not settled yet → page is still loading, report clean
		if (!isSettled) {
			setHasChanges(false);
			return;
		}

		const currentItemsStr = JSON.stringify(orderItems);
		const itemsChanged = savedItemsBaseline !== currentItemsStr;

		const driverTipChanged = parseFloat(driverTip || 0) !== parseFloat(initialDriverTip || 0);
		const deliveryFeeChanged = parseFloat(deliveryFee || 0) !== parseFloat(initialDeliveryFee || 0);
		const addressChanged = pendingAddress !== null && pendingAddress !== undefined;
		const couponChanged = (selectedCouponId || null) !== (initialCouponId || null);

		const hasAnyChanges = itemsChanged || driverTipChanged || deliveryFeeChanged || addressChanged || couponChanged;

		setHasChanges(hasAnyChanges);
	}, [isSettled, forcedChanges, orderItems, savedItemsBaseline, driverTip, initialDriverTip, deliveryFee, initialDeliveryFee, pendingAddress, selectedCouponId, initialCouponId]);

	// Clear validation errors when user makes changes to fix them
	const clearFieldError = useCallback((itemId, field) => {
		setValidationErrors((prev) => {
			const updated = {...prev};
			if (updated[itemId]) {
				const itemErrors = {...updated[itemId]};
				delete itemErrors[field];
				if (Object.keys(itemErrors).length === 0) {
					delete updated[itemId];
				} else {
					updated[itemId] = itemErrors;
				}
			}
			return updated;
		});
	}, []);

	const handleQuantityChange = useCallback((id, delta) => {
		setOrderItems((items) =>
			items.map((item) =>
				item.id === id
					? {...item, quantity: Math.max(0, item.quantity + delta)}
					: item
			)
		);
		clearFieldError(id, 'quantity');
	}, [clearFieldError]);

	const handleStatusChange = useCallback((id, status) => {
		setOrderItems((items) =>
			items.map((item) => (item.id === id ? {...item, status} : item))
		);
		clearFieldError(id, 'status');
	}, [clearFieldError]);

	const handleCancelReasonChange = useCallback((id, reason) => {
		setOrderItems((items) =>
			items.map((item) =>
				item.id === id ? {...item, cancelReason: reason} : item
			)
		);
		clearFieldError(id, 'cancelReason');
	}, [clearFieldError]);

	const handleCommentChange = useCallback((id, comment) => {
		setOrderItems((items) =>
			items.map((item) => (item.id === id ? {...item, comment} : item))
		);
	}, []);

	const handlePriceChange = useCallback((id, field, value) => {
		setOrderItems((items) =>
			items.map((item) => {
				if (item.id === id) {
					const parsedValue = parseFloat(value) || 0;
					const updates = {[field]: parsedValue};

					// If editing coupon_discount_amount, ALWAYS mark as manual
					// User explicitly edited the field, so respect their intent
					// Whether they set it to 0, 5, or any value - it's a manual edit
					if (field === 'coupon_discount_amount') {
						updates.is_manual_discount = true;
					}

					return {...item, ...updates};
				}
				return item;
			})
		);
		clearFieldError(id, field);
	}, [clearFieldError]);

	// Clear all coupon discounts from all items
	const handleClearAllCouponDiscounts = useCallback(() => {
		setOrderItems((items) =>
			items.map((item) => ({
				...item,
				coupon_discount_amount: 0,
				is_manual_discount: false // Reset to auto-coupon mode when clearing
			}))
		);
		// Force hasChanges to true since we're removing order-level coupon
		// Use forcedChanges flag to prevent useEffect from overwriting this
		setForcedChanges(true);
		setHasChanges(true);
	}, []);

	// Reset item discount to auto-coupon mode (removes manual flag)
	const handleResetItemToAutoCoupon = useCallback((id) => {
		setOrderItems((items) =>
			items.map((item) =>
				item.id === id
					? {
						...item,
						coupon_discount_amount: 0,
						is_manual_discount: false // Reset to auto-coupon mode
					}
					: item
			)
		);
		alert.toast('Item reset to auto-coupon mode. Click "Recalculate" to apply coupon.', 'info');
	}, [alert]);

	/**
	 * Force hasChanges flag to true
	 * Used when external changes happen (like coupon selection)
	 */
	const forceHasChanges = useCallback(() => {
		setForcedChanges(true);
		setHasChanges(true);
	}, []);

// Check for active promotion
	const now = new Date();
	const handleReplaceItem = useCallback((itemToReplace, newProduct) => {
		if (!newProduct) {
			// If no product provided, just return the item to replace for modal
			return orderItems.find(item => item.id === itemToReplace);
		}

		// Update local state only - will be saved when clicking Save button
		setOrderItems((items) => {
			// Generate a new temporary ID for the replacement item
			const newId = Math.max(...items.map(i => i.id), 0) + 1;
			const tempOrderItemId = `new_${Date.now()}_${newId}`;

			// Find the index of the item being replaced
			const replacedItemIndex = items.findIndex(item => item.id === itemToReplace.id);

			// Use the selling price as both item_price and original_price
			// Coupon discount will be calculated separately when recalculate is clicked
			let sellingPrice = parseFloat(newProduct.price || newProduct.item_price || 0);

			let originalPrice = null; // Default to null (only set if promotion exists)

			if (
				newProduct.promo &&
				newProduct.promo_price &&
				newProduct.promo_start_date &&
				newProduct.promo_end_date
			) {
				const promoStart = new Date(newProduct.promo_start_date);
				const promoEnd = new Date(newProduct.promo_end_date);

				if (now >= promoStart && now <= promoEnd) {
					sellingPrice = newProduct.promo_price;
					originalPrice = parseFloat(newProduct.price || newProduct.item_price || 0); // Only set when promotion is active
				}
			}
			// Create the new replacement item
			const newItem = {
				id: newId,
				order_item_id: tempOrderItemId, // Temporary ID for new item
				item_id: newProduct.id || newProduct.item_id || newProduct.product_id,
				item_name:
					(newProduct?.name || newProduct?.product_name || 'New Product') +
					(newProduct?.list_brands && newProduct.list_brands.length > 0 ? ` - ${newProduct.list_brands[0].name}` : ''),
				item_price_name: newProduct.priceName || newProduct.item_price_name || newProduct.weight || '',
				quantity: itemToReplace.quantity || 1,
				item_price: sellingPrice,
				original_price: originalPrice, // Same as item_price initially, no strikethrough
				coupon_discount_amount: 0,
				status: 'new',
				cancelReason: '',
				comment: 'Replacement item',
				originalPrice: originalPrice, // Same as item_price initially
				replaced_from: itemToReplace.order_item_id, // Track which item this replaced
				replacement_items: [],
				isNew: true, // Flag to indicate this is a new item not yet saved
				isReplacement: true, // Flag to indicate this is a replacement
				originalItemId: itemToReplace.order_item_id, // Original item being replaced
				product_code: newProduct.product_code || newProduct.sku || '',
				item_price_type_id: newProduct.itemPriceTypeId || newProduct.item_price_type_id,
				store_item_info_id: newProduct.storeItemInfoId || newProduct.store_item_info_id,
				// Snapshot the product's VAT rate so the row shows it before the item is saved
				vat_rate: getProductVatRate(newProduct),
			};

			// Create updated items array with replacement inserted right after the cancelled item
			const updatedItems = items.map((item) =>
				item.id === itemToReplace.id
					? {...item, status: 'cancelled', cancelReason: 'Replaced'}
					: item
			);

			// Insert the replacement item right after the cancelled item to maintain position
			updatedItems.splice(replacedItemIndex + 1, 0, newItem);

			return updatedItems;
		});

		pendingRecalcRef.current = true;
		alert.toast(`Item replaced with ${newProduct.name || newProduct.product_name}. Recalculating totals...`, 'info');
	}, [orderItems, alert]);

	const handleCancelItem = useCallback(async (id) => {
		const result = await alert.confirm(
			'Cancel Item',
			'Are you sure you want to cancel this item?',
			'Yes, Cancel',
			'No'
		);

		if (result.isConfirmed) {
			setOrderItems((items) =>
				items.map((item) =>
					item.id === id ? {...item, status: 'cancelled'} : item
				)
			);
			alert.toast('Item marked as cancelled', 'success');
		}
	}, [alert]);

	const handleRestoreItem = useCallback(async (id) => {
		const item = orderItems.find(item => item.id === id);
		if (!item) {
			alert.toast('Item not found', 'error');
			return;
		}

		if (!orderId) {
			alert.toast('Order ID is required to restore items', 'warning');
			return;
		}

		// Check if item has replacement
		const hasReplacement = item.replacement_items && item.replacement_items.length > 0;

		const confirmMessage = hasReplacement
			? 'This item was replaced. Restoring it will cancel the replacement. Continue?'
			: 'Are you sure you want to restore this item?';

		const result = await alert.confirm(
			'Restore Item',
			confirmMessage,
			'Yes, Restore',
			'Cancel'
		);

		if (result.isConfirmed) {
			try {
				const response = await API.restoreOrderItem(orderId, item.order_item_id);

				if (response && response.success) {
					// Refresh order details to get updated items
					if (fetchOrderDetails) {
						await fetchOrderDetails();
					}

					// Refresh order summary
					if (refreshOrder) {
						await refreshOrder();
					}

					const message = response.data.cancelled_replacement
						? 'Item restored and replacement cancelled!'
						: 'Item restored successfully!';

					alert.toast(message, 'success');
				} else {
					throw new Error(response?.message || response?.data?.message || 'Restore failed');
				}
			} catch (error) {
				alert.toast(`Failed to restore item: ${error.message || error}`, 'error');
			}
		}
	}, [orderId, alert, fetchOrderDetails, orderItems]);

	const handleDeleteReplacement = useCallback(async (id) => {
		const item = orderItems.find(item => item.id === id);
		if (!item) {
			alert.toast('Item not found', 'error');
			return;
		}

		const result = await alert.confirm(
			'Delete Replacement Item',
			'Are you sure you want to delete this replacement item?',
			'Yes, Delete',
			'Cancel'
		);

		if (result.isConfirmed) {
			// If item is new (not yet saved), just remove from local state
			if (item.isNew) {
				setOrderItems((items) => {
					// Remove the new item
					const updatedItems = items.filter(i => i.id !== id);

					// If this was a replacement, restore the original item to its original status
					if (item.isReplacement && item.originalItemId) {
						return updatedItems.map(i => {
							if (i.order_item_id === item.originalItemId) {
								// Restore the item: if it was cancelled for replacement, restore to 'new'
								// Otherwise keep its current status
								const newStatus = i.cancelReason === 'Replaced' ? 'new' : i.status;
								return {
									...i,
									status: newStatus,
									cancelReason: i.cancelReason === 'Replaced' ? '' : i.cancelReason
								};
							}
							return i;
						});
					}

					return updatedItems;
				});

				alert.toast('Item removed', 'success');
				return;
			}

			// For existing items, call the API
			if (!orderId) {
				alert.toast('Order ID is required to delete items', 'warning');
				return;
			}

			try {
				const response = await API.deleteOrderItem(orderId, item.order_item_id);

				if (response && response.success) {
					// Refresh order details to get updated items
					if (fetchOrderDetails) {
						await fetchOrderDetails();
					}

					// Refresh order summary
					if (refreshOrder) {
						await refreshOrder();
					}

					alert.toast('Replacement item deleted successfully!', 'success');
				} else {
					throw new Error(response?.message || response?.data?.message || 'Delete failed');
				}
			} catch (error) {
				alert.toast(`Failed to delete item: ${error.message || error}`, 'error');
			}
		}
	}, [orderId, alert, fetchOrderDetails, orderItems]);

	const handleAddProduct = useCallback((product, quantity = 1) => {
		if (!product) {
			// If no product provided, just return null (used to trigger modal)
			return null;
		}

		// Update local state only - will be saved when clicking Save button
		setOrderItems((items) => {
			// Generate a new temporary ID for the new item
			const newId = Math.max(...items.map(i => i.id), 0) + 1;
			const tempOrderItemId = `new_${Date.now()}_${newId}`;

			// Use the selling price as both item_price and original_price
			// Coupon discount will be calculated separately when recalculate is clicked
			let sellingPrice = parseFloat(product.price || product.item_price || 0);

			let originalPrice = null; // Default to null (only set if promotion exists)

			if (
				product.promo &&
				product.promo_price &&
				product.promo_start_date &&
				product.promo_end_date
			) {
				const promoStart = new Date(product.promo_start_date);
				const promoEnd = new Date(product.promo_end_date);

				if (now >= promoStart && now <= promoEnd) {
					sellingPrice = product.promo_price;
					originalPrice = parseFloat(product.price || product.item_price || 0); // Only set when promotion is active
				}
			}
			const newItem = {
				id: newId,
				order_item_id: tempOrderItemId, // Temporary ID for new item
				item_id: product.id || product.item_id || product.product_id,
				item_name:
					(product.name || product.product_name || 'New Product') +
					(product?.list_brands && product.list_brands.length > 0 ? ` - ${product.list_brands[0].name}` : ''),
				item_price_name: product.priceName || product.item_price_name || product.weight || '',
				quantity: quantity,
				item_price: sellingPrice,
				original_price: originalPrice, // Same as item_price initially, no strikethrough
				coupon_discount_amount: 0,
				status: 'new',
				cancelReason: '',
				comment: '',
				originalPrice: originalPrice, // null if no promo, regular price if promo is active
				replaced_from: null,
				replacement_items: [],
				isNew: true, // Flag to indicate this is a new item not yet saved
				isReplacement: false,
				product_code: product.product_code || product.sku || '',
				item_price_type_id: product.itemPriceTypeId || product.item_price_type_id,
				store_item_info_id: product.storeItemInfoId || product.store_item_info_id,
				// Snapshot the product's VAT rate so the row shows it before the item is saved
				vat_rate: getProductVatRate(product),
			};

			return [...items, newItem];
		});

		pendingRecalcRef.current = true;
		alert.toast(`Product "${product.name || product.product_name}" added. Recalculating totals...`, 'info');
	}, [alert]);

	const handleRecalculate = useCallback(async () => {
		if (!orderId) {
			alert.toast('Order ID is required to recalculate', 'warning');
			return;
		}

		setIsRecalculating(true);
		try {
			// Prepare items data - include all fields that affect calculation
			const itemsData = orderItems.map((item) => ({
				order_item_id: item.order_item_id,
				item_id: item.item_id,
				quantity: item.quantity,
				item_price: item.item_price,
				coupon_discount_amount: item.coupon_discount_amount || 0,
				item_status: mapStringStatusToItemStatus(item.status),
				cancel_reason: item.cancelReason || null,
				comment: item.comment || null,
				original_price: item.originalPrice ?? null,
				is_new: item.isNew || false, // Flag to indicate new items not yet saved
				is_manual_discount: item.is_manual_discount || false, // Flag to exclude from coupon recalculation
			}));

			// Call API to recalculate
			const payload = {
				items: itemsData,
				coupon_id: selectedCouponId || null,
				driver_tip: driverTip !== undefined && driverTip !== null ? parseFloat(driverTip) : 0, // Include driver tip for remaining calculation
			};

			// Include delivery_address_id if address was changed (to recalculate delivery fee)
			if (pendingAddress && pendingAddress.address_id) {
				payload.delivery_address_id = pendingAddress.address_id;
				// When address changes, DON'T send delivery_fee - let backend calculate it
				// The delivery_fee should only be sent when admin manually overrides it
			} else if (deliveryFee !== undefined && deliveryFee !== null) {
				// Only include delivery_fee if NO address change (manual override by admin)
				payload.delivery_fee = parseFloat(deliveryFee);
			}

			const response = await API.recalculateOrder(orderId, payload);

			if (response && response.success) {
				// Update items with distributed discounts from API
				// The backend uses a "hybrid mode" where it preserves manual edits if sent in the request
				// Backend returns is_manual_discount flag for each item
				if (response.data?.items_with_discounts) {
					const discountMap = new Map();
					response.data.items_with_discounts.forEach(d => {
						if (d.order_item_id) {
							discountMap.set(d.order_item_id.toString(), {
								coupon_discount_amount: d.coupon_discount_amount,
								is_manual_discount: d.is_manual_discount !== undefined ? d.is_manual_discount : false,
							});
						}
					});

					setOrderItems(prevItems => prevItems.map(item => {
						// Use order_item_id as key (works for both existing and new items since we send temp ID)
						const updatedData = discountMap.get(item.order_item_id?.toString());

						if (updatedData) {
							// Backend returned updated discount and flag for this item
							return {
								...item,
								coupon_discount_amount: updatedData.coupon_discount_amount,
								is_manual_discount: updatedData.is_manual_discount,
							};
						}
						// No change for this item (likely excluded from calculation)
						return item;
					}));
				}

				// Show summary message with discount info
				const totalDiscount = response.data.coupon_discount || 0;
				if (totalDiscount > 0) {
					alert.toast(`Coupon discount: $${totalDiscount.toFixed(2)} distributed across items (not saved yet)`, 'success');
				} else {
					alert.toast('Order totals recalculated successfully! (not saved yet)', 'success');
				}

				// Check for coupon validation errors
				if (response.data?.couponValid === false && response.data?.couponError) {
					alert.toast(`Warning: ${response.data.couponError}`, 'warning');
				}

				// Update totals in parent component if callback is provided
				if (onTotalsChange && response.data) {
					const totalsToSend = {
						item_total: response.data.item_total || response.data.subtotal || 0,
						delivery_fee: response.data.delivery_fee || 0,
						coupon_discount_amount: response.data.coupon_discount || 0,
						grand_total: response.data.grand_total || 0,
					};
					onTotalsChange(totalsToSend);
				}

				// Update orderSummery state if callback is provided
				if (onOrderSummeryChange && response.data) {
					onOrderSummeryChange({
						item_total: response.data.item_total || response.data.subtotal || 0,
						delivery_fee: response.data.delivery_fee || 0,
						coupon_discount_amount: response.data.coupon_discount || 0,
						order_total: response.data.grand_total || 0,
						total_kms: response.data.total_kms || 0,
						driver_tip: response.data.driver_tip !== undefined ? response.data.driver_tip : driverTip,
						// Keep existing already_paid_amount
						// payment_amount and remaining_amount will be recalculated in component
					});
				}

				hasRecalculatedRef.current = true;

			} else {
				throw new Error(response?.message || response?.data?.message || 'Recalculation failed');
			}

		} catch (error) {
			alert.toast(`Failed to recalculate: ${error.message || error}`, 'error');
		} finally {
			setIsRecalculating(false);
		}
	}, [orderItems, orderId, alert, selectedCouponId, deliveryFee, pendingAddress, onTotalsChange, onOrderSummeryChange]);

	// Store handleRecalculate in ref so other handlers can access it
	useEffect(() => {
		recalculateRef.current = handleRecalculate;
	}, [handleRecalculate]);

	const handleDiscard = useCallback(async () => {
		if (!hasChanges) {
			alert.toast('No changes to discard', 'info');
			return;
		}

		const result = await alert.confirm(
			'Discard Changes',
			'Are you sure you want to discard all changes?',
			'Yes, Discard',
			'Cancel'
		);

		if (result.isConfirmed) {
			// Reset order items to saved baseline state
			setOrderItems(JSON.parse(savedItemsBaseline));

			// Reset driver tip to initial value
			if (onDriverTipChange) {
				onDriverTipChange(initialDriverTip);
			}

			// Reset delivery fee to initial value
			if (onDeliveryFeeChange) {
				onDeliveryFeeChange(initialDeliveryFee);
			}

			// Clear pending address change
			if (onAddressSaved) {
				onAddressSaved(); // This clears pendingAddress in parent
			}

			// Reset calculated totals (clears recalculated coupon/delivery fee changes)
			if (onTotalsChange) {
				onTotalsChange(null);
			}

			// Reset order summery (clears recalculated values)
			if (onOrderSummeryChange) {
				onOrderSummeryChange(null);
			}

			setForcedChanges(false); // Reset forced changes flag
			setHasChanges(false);
			hasRecalculatedRef.current = false; // Reset recalculate flag on discard
			setValidationErrors({}); // Clear validation errors when discarding
			alert.toast('Changes discarded successfully', 'success');

			// Refresh the order to get fresh data from server
			if (refreshOrder) {
				await refreshOrder();
			}
		}
	}, [savedItemsBaseline, hasChanges, initialDriverTip, initialDeliveryFee, onDriverTipChange, onDeliveryFeeChange, onAddressSaved, onTotalsChange, onOrderSummeryChange, refreshOrder, alert]);

	const handleSave = useCallback(async () => {
		if (!hasChanges) {
			alert.toast('No changes to save', 'info');
			return;
		}

		if (!orderId) {
			alert.toast('Order ID is required to save changes', 'warning');
			return;
		}

		// Validate all items before saving
		const {itemErrors, hasErrors} = validateOrderItems(orderItems);

		if (hasErrors) {
			setValidationErrors(itemErrors);
			const errorMessage = formatValidationErrors(itemErrors, orderItems);
			alert.toast(errorMessage, 'error');

			// Scroll to first error
			const firstErrorItemId = Object.keys(itemErrors)[0];
			const errorElement = document.querySelector(`[data-item-id="${firstErrorItemId}"]`);
			if (errorElement) {
				errorElement.scrollIntoView({behavior: 'smooth', block: 'center'});
			}

			return;
		}

		// Clear any previous validation errors
		setValidationErrors({});
		setIsSaving(true);

		try {
			// Separate new items from existing items
			const newItems = orderItems.filter(item => item.isNew);
			const existingItems = orderItems.filter(item => !item.isNew);

			// Process new replacement items first
			const replacementItems = newItems.filter(item => item.isReplacement);
			for (const item of replacementItems) {
				const response = await API.replaceOrderItem(orderId, item.originalItemId, {
					product_id: item.item_id,
					item_price_type_id: item.item_price_type_id,
					store_item_info_id: item.store_item_info_id,
					quantity: item.quantity,
					item_price: item.item_price,
				});
				if (!response?.success) {
					throw new Error(response?.message || 'Failed to create replacement item');
				}
			}

			// Process new added items (not replacements)
			const addedItems = newItems.filter(item => !item.isReplacement);
			for (const item of addedItems) {
				const response = await API.addOrderItem(orderId, {
					product_id: item.item_id,
					item_price_type_id: item.item_price_type_id,
					store_item_info_id: item.store_item_info_id,
					quantity: item.quantity,
					item_price: item.item_price,
				});
				if (!response?.success) {
					throw new Error(response?.message || 'Failed to add new item');
				}
			}

			// Prepare data for existing items update
			const updateData = {
				items: existingItems.map((item) => ({
					order_item_id: item.order_item_id,
					item_id: item.item_id,
					quantity: item.quantity,
					item_status: mapStringStatusToItemStatus(item.status),
					cancel_reason: item.cancelReason || null,
					comment: item.comment || null,
					item_price: item.item_price,
					original_price: item.originalPrice ?? null,
					coupon_discount_amount: item.coupon_discount_amount || 0,
					is_manual_discount: item.is_manual_discount || false, // Preserve manual discount flag
				})),
				driver_tip: driverTip !== undefined && driverTip !== null ? parseFloat(driverTip) : null,
				delivery_fee: deliveryFee !== undefined && deliveryFee !== null ? parseFloat(deliveryFee) : null,
				// Include pending address if changed
				address_id: pendingAddress?.address_id || null,
				// Always include coupon_id to update order-level coupon discount
				coupon_id: selectedCouponId || null,
				skip_coupon_redistribution: !hasRecalculatedRef.current,
			};


			
			const response = await API.updateOrderItems(orderId, updateData);

			if (response && response.success) {
				// Update to new saved state
				setLastSaved(new Date());

				// Update initial driver tip to current value after successful save
				setInitialDriverTip(parseFloat(driverTip || 0));

				// Update initial delivery fee to current value after successful save
				setInitialDeliveryFee(parseFloat(deliveryFee || 0));

				// Update initial coupon ID to current value after successful save
				// This includes setting to null if coupon was removed
				if (selectedCouponId !== initialCouponId) {
					setInitialCouponId(selectedCouponId);
				}

				// Clear pending address after successful save
				if (pendingAddress && onAddressSaved) {
					onAddressSaved();
				}

				// Reset forced changes flag after successful save
				setForcedChanges(false);

				hasRecalculatedRef.current = false;

				alert.toast('Order changes saved successfully!', 'success');

				// IMPORTANT: Update items with fresh data from response (includes coupon discounts)
				// The response contains the updated order with recalculated coupon discounts
				const updatedOrder = response.data?.order || response.order;

				if (updatedOrder && updatedOrder.order_item_details) {
					const updatedItemsFromServer = updatedOrder.order_item_details;

					// Map the response items to match our state structure
					const refreshedItems = mapItemDetails(updatedItemsFromServer);

					setOrderItems(refreshedItems);
					setSavedItemsBaseline(JSON.stringify(refreshedItems));
				} else {
					// Fallback: Refresh order details from API if response doesn't include items
					if (fetchOrderDetails) {
						await fetchOrderDetails();
					}
				}

				// Refresh order summary
				if (refreshOrder) {
					await refreshOrder();
				}
			} else {

				// Check if response contains validation errors from backend
				if (response?.data?.errors || response?.errors) {
					const backendErrors = response?.data?.errors || response?.errors;
					// Handle backend validation errors if they exist
					throw new Error(response?.message || response?.data?.message || 'Validation failed');
				} else {
					throw new Error(response?.message || response?.data?.message || 'Save failed');
				}
			}

		} catch (error) {

			// More detailed error message
			let errorMessage = 'Failed to save order changes';
			if (error.message) {
				errorMessage = error.message;
			} else if (error.response?.data?.message) {
				errorMessage = error.response.data.message;
			} else if (typeof error === 'string') {
				errorMessage = error;
			}

			alert.toast(`Failed to save: ${errorMessage}`, 'error');
		} finally {
			setIsSaving(false);
		}
	}, [orderItems, orderId, hasChanges, driverTip, deliveryFee, alert, selectedCouponId, pendingAddress, onAddressSaved, fetchOrderDetails, refreshOrder]);

	const handleCancelOrder = useCallback(async () => {
		if (!orderId) {
			alert.toast('Order ID is required to cancel the order', 'warning');
			return;
		}

		const result = await alert.confirm(
			'Cancel Order',
			'Are you sure you want to cancel the entire order? This action cannot be undone.',
			'Yes, Cancel Order',
			'No'
		);

		if (result.isConfirmed) {
			try {
				const response = await API.cancelOrderAdmin(orderId);

				if (response && response.success) {
					alert.toast('Order cancelled successfully!', 'success');

					// Redirect back to orders list
					setTimeout(() => {
						window.location.href = '/admin/orders';
					}, 1500);
				} else {
					throw new Error(response?.message || response?.data?.message || 'Cancel failed');
				}

			} catch (error) {
				alert.toast(`Failed to cancel order: ${error.message || error}`, 'error');
			}
		}
	}, [orderId, alert]);

	return {
		orderItems,
		hasChanges,
		isSaving,
		isLoading,
		isRecalculating,
		lastSaved,
		validationErrors,
		fetchOrderDetails,
		forceHasChanges,  // Export at top level for easy access
		handlers: {
			handleQuantityChange,
			handleStatusChange,
			handleCancelReasonChange,
			handleCommentChange,
			handlePriceChange,
			handleReplaceItem,
			handleCancelItem,
			handleRestoreItem,
			handleDeleteReplacement,
			handleAddProduct,
			handleRecalculate,
			handleDiscard,
			handleSave,
			handleCancelOrder,
			handleClearAllCouponDiscounts,
			handleResetItemToAutoCoupon,
		},
		utils: {
			getFieldError: (itemId, field) => getFieldError(validationErrors, itemId, field),
			clearFieldError,
		},
	};
};

/**
 * Hook for calculating order totals
 */
export const useOrderTotals = (orderItems) => {
	const calculateItemTotal = useCallback((item) => {
		// Subtotal should NOT include coupon discount
		return item.quantity * item.item_price;
	}, []);

	const totals = useMemo(() => {
		const activeItems = orderItems.filter((item) => item.status !== 'cancelled');

		return {
			totalQuantity: activeItems.reduce((sum, item) => sum + item.quantity, 0),
			// Subtotal (item_total) excludes coupon discount
			totalPrice: activeItems.reduce(
				(sum, item) => sum + calculateItemTotal(item),
				0
			),
		};
	}, [orderItems, calculateItemTotal]);

	return {...totals, calculateItemTotal};
};

// Export mapping functions for external use
export {mapItemStatusToString, mapStringStatusToItemStatus};
